import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { useAuth } from '@/context/AuthContext';
import { NavLink, useLocation } from 'react-router-dom';
import { useSites } from '@/hooks/useSettings';
import { useTour } from '@/context/TourContext';
import {
  LayoutDashboard, ClipboardList, Package, Cpu, AlertTriangle,
  Truck, Shield, FileText, BarChart3, Settings, Search,
  ScanBarcode, Bell, User, MapPin, Recycle, ChevronDown, Menu, X,
  BoxesIcon, Users, Handshake, CalendarDays, Contact,
  Mail, Bot, Plug, Building2, Plus,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const TOUR_ATTR_MAP: Record<string, string> = {
  '/dashboard': 'nav-dashboard',
  '/work-orders': 'nav-work-orders',
  '/assets': 'nav-assets',
  '/processing': 'nav-processing',
  '/compliance': 'nav-compliance',
};
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const DISABLED_ROUTES = new Set(['/exceptions', '/inventory', '/shipments', '/contacts', '/documents', '/reports']);

interface NavItem {
  to: string;
  icon: typeof LayoutDashboard;
  label: string;
  group: 'Operations' | 'Processing' | 'Logistics' | 'CRM' | 'Admin';
}

const BASE_NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', group: 'Operations' },
  { to: '/work-orders', icon: ClipboardList, label: 'Work Orders', group: 'Operations' },
  { to: '/containers', icon: BoxesIcon, label: 'Containers', group: 'Operations' },
  { to: '/assets', icon: Package, label: 'Assets', group: 'Operations' },
  { to: '/processing', icon: Cpu, label: 'Processing', group: 'Processing' },
  { to: '/exceptions', icon: AlertTriangle, label: 'Exceptions', group: 'Processing' },
  { to: '/inventory', icon: Package, label: 'Inventory', group: 'Logistics' },
  { to: '/shipments', icon: Truck, label: 'Shipments', group: 'Logistics' },
  { to: '/contacts', icon: Users, label: 'Contacts', group: 'Logistics' },
  { to: '/compliance', icon: Shield, label: 'Compliance', group: 'Admin' },
  { to: '/documents', icon: FileText, label: 'Documents', group: 'Admin' },
  { to: '/reports', icon: BarChart3, label: 'Reports', group: 'Admin' },
  { to: '/admin/users', icon: Users, label: 'Users', group: 'Admin' },
  { to: '/admin/tenants', icon: Building2, label: 'Organizations', group: 'Admin' },
  { to: '/admin/settings', icon: Settings, label: 'Settings', group: 'Admin' },
  { to: '/admin/email', icon: Mail, label: 'Email', group: 'Admin' },
  { to: '/admin/integrations', icon: Plug, label: 'Integrations', group: 'Admin' },
];

const CRM_NAV_ITEMS: NavItem[] = [
  { to: '/crm', icon: Contact, label: 'CRM Dashboard', group: 'CRM' },
  { to: '/crm/customers', icon: Building2, label: 'Customers', group: 'CRM' },
  { to: '/crm/contacts', icon: Users, label: 'Contacts', group: 'CRM' },
  { to: '/crm/deals', icon: Handshake, label: 'Deals', group: 'CRM' },
  { to: '/crm/appointments', icon: CalendarDays, label: 'Appointments', group: 'CRM' },
];

const VAPI_NAV_ITEM: NavItem = { to: '/admin/vapi', icon: Bot, label: 'VAPI Agent', group: 'Admin' };

const GROUP_ORDER = ['Operations', 'Processing', 'Logistics', 'CRM', 'Admin'] as const;

export default function TopNavBar() {
  const { currentLocationId, setCurrentLocationId, setScanModalOpen } = useApp();
  const { setShowChoiceDialog } = useTour();
  const { user, logout } = useAuth();
  const { data: rawSites } = useSites();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const sites = Array.isArray(rawSites) ? rawSites : [];

  const isSuperAdmin = user?.is_super_admin ?? false;
  const crmEnabled = isSuperAdmin || !!user?.tenant?.features?.crm_enabled;
  const vapiEnabled = isSuperAdmin || !!user?.tenant?.features?.vapi_enabled;
  const allNavItems = useMemo(
    () => [
      ...BASE_NAV_ITEMS,
      ...(crmEnabled ? CRM_NAV_ITEMS : []),
      ...(vapiEnabled ? [VAPI_NAV_ITEM] : []),
    ],
    [crmEnabled, vapiEnabled]
  );

  const groupedItems = useMemo(() => {
    const map = new Map<string, NavItem[]>();
    for (const item of allNavItems) {
      const list = map.get(item.group) ?? [];
      list.push(item);
      map.set(item.group, list);
    }
    return map;
  }, [allNavItems]);

  const visibleGroups = useMemo(
    () => GROUP_ORDER.filter((g) => groupedItems.has(g)),
    [groupedItems]
  );

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const groupHasActive = (group: string) =>
    (groupedItems.get(group) ?? []).some((item) => isActive(item.to));

  return (
    <header className="sticky top-0 z-50 border-b border-sidebar-border bg-sidebar text-sidebar-foreground">
      {/* Top utility row */}
      <div className="flex h-14 items-center gap-3 px-4">
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 text-sidebar-foreground hover:bg-sidebar-accent md:hidden"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>

        <div className="mr-2 flex items-center gap-2 md:mr-4">
          <Recycle className="h-6 w-6 shrink-0 text-sidebar-primary" />
          <span className="hidden text-lg font-bold tracking-tight text-sidebar-primary-foreground sm:inline">
            EcoTech
          </span>
        </div>

        <div className="relative hidden max-w-sm flex-1 md:block">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sidebar-foreground/50" />
          <input
            type="text"
            placeholder="Search work orders, assets, serials..."
            className="h-9 w-full rounded-md border border-sidebar-border bg-sidebar-accent pl-9 pr-3 text-sm text-sidebar-accent-foreground placeholder:text-sidebar-foreground/50 focus:outline-none focus:ring-2 focus:ring-sidebar-ring"
          />
        </div>

        <div className="ml-auto flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-9 gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => navigate('/work-orders/new')}>
            <Plus className="h-4 w-4" />
            <span className="hidden lg:inline">Create WO</span>
          </Button>

          <Button data-tour="scan-button" variant="outline" size="sm" className="h-9 gap-1.5 border-sidebar-border bg-sidebar-accent text-sidebar-accent-foreground hover:bg-sidebar-border hover:text-sidebar-accent-foreground" onClick={() => setScanModalOpen(true)}>
            <ScanBarcode className="h-4 w-4" />
            <span className="hidden lg:inline">Scan</span>
          </Button>

          <Select value={currentLocationId} onValueChange={setCurrentLocationId}>
            <SelectTrigger data-tour="location-selector" className="h-9 w-auto min-w-[100px] gap-1.5 border-sidebar-border bg-sidebar-accent text-sm text-sidebar-accent-foreground md:min-w-[140px]">
              <MapPin className="h-3.5 w-3.5 text-sidebar-foreground/50" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sites.map((site) => (
                <SelectItem key={site.id} value={site.id}>
                  {site.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="ghost" size="icon" className="relative h-9 w-9 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground">
            <Bell className="h-4 w-4" />
            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-destructive" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="hidden h-9 w-9 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground sm:flex">
                <User className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[160px]">
              <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                {user?.email}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/profile/settings')}>
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { localStorage.removeItem('erp_tour_completed'); localStorage.removeItem('erp_tour_dismissed'); setShowChoiceDialog(true); }}>
                Take a Tour
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="text-destructive focus:text-destructive">
                <button type="button" onClick={async () => { await logout(); window.location.replace('/login'); }} className="w-full text-left">
                  Log out
                </button>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Desktop grouped dropdown nav */}
      <nav className="hidden items-center gap-1 px-4 md:flex">
        {visibleGroups.map((group) => {
          const groupTourMap: Record<string, string> = {
            Operations: 'nav-operations',
            Processing: 'nav-processing-group',
            Logistics: 'nav-logistics',
            CRM: 'nav-crm-group',
            Admin: 'nav-admin',
          };
          return (
          <DropdownMenu key={group}>
            <DropdownMenuTrigger asChild>
              <button
                data-tour={groupTourMap[group]}
                className={cn(
                  'flex items-center gap-1.5 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors',
                  groupHasActive(group)
                    ? 'border-sidebar-primary text-sidebar-primary'
                    : 'border-transparent text-sidebar-foreground/70 hover:border-sidebar-border hover:text-sidebar-accent-foreground'
                )}
              >
                {group}
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[180px]">
              {(groupedItems.get(group) ?? []).map((item) => {
                const disabled = DISABLED_ROUTES.has(item.to);
                return (
                  <DropdownMenuItem key={item.to} disabled={disabled} asChild={!disabled}>
                    {disabled ? (
                      <span className="flex items-center gap-2 opacity-50">
                        <item.icon className="h-4 w-4" />
                        {item.label}
                        <span className="ml-auto text-[10px] text-muted-foreground">Soon</span>
                      </span>
                    ) : (
                      <NavLink
                        to={item.to}
                        className={cn('flex items-center gap-2', isActive(item.to) && 'font-semibold text-sidebar-primary')}
                        {...(TOUR_ATTR_MAP[item.to] ? { 'data-tour': TOUR_ATTR_MAP[item.to] } : {})}
                      >
                        <item.icon className="h-4 w-4" />
                        {item.label}
                      </NavLink>
                    )}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          );
        })}
      </nav>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 top-14 z-40 bg-black/40 md:hidden" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 right-0 top-14 z-50 animate-in slide-in-from-top-2 border-b border-sidebar-border bg-sidebar duration-200 md:hidden">
            <div className="border-b border-sidebar-border px-4 py-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sidebar-foreground/50" />
                <input
                  type="text"
                  placeholder="Search work orders, assets, serials..."
                  className="h-10 w-full rounded-md border border-sidebar-border bg-sidebar-accent pl-9 pr-3 text-sm text-sidebar-accent-foreground placeholder:text-sidebar-foreground/50 focus:outline-none focus:ring-2 focus:ring-sidebar-ring"
                />
              </div>
            </div>
            <nav className="max-h-[60vh] overflow-y-auto px-2 py-2">
              {visibleGroups.map((group) => (
                <div key={group} className="mb-2">
                  <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-sidebar-foreground/40">
                    {group}
                  </p>
                  {(groupedItems.get(group) ?? []).map((item) => {
                    const disabled = DISABLED_ROUTES.has(item.to);
                    return disabled ? (
                      <span
                        key={item.to}
                        className="flex cursor-not-allowed items-center gap-3 rounded-md px-3 py-3 text-sm font-medium opacity-40"
                      >
                        <item.icon className="h-5 w-5" />
                        <span>{item.label}</span>
                        <span className="ml-auto text-[10px] text-muted-foreground">Coming soon</span>
                      </span>
                    ) : (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={() => setMobileOpen(false)}
                        className={cn(
                          'flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-colors',
                          isActive(item.to)
                            ? 'bg-sidebar-accent text-sidebar-primary'
                            : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                        )}
                        {...(TOUR_ATTR_MAP[item.to] ? { 'data-tour': TOUR_ATTR_MAP[item.to] } : {})}
                      >
                        <item.icon className="h-5 w-5" />
                        <span>{item.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              ))}
            </nav>
          </div>
        </>
      )}
    </header>
  );
}
